/**
 * Busy state for buttons - the house pattern for "working..." affordance
 * (the states standard, /standards/states).
 *
 *   g.busy(btn, true)   - disable + spinner (keeps the button's width)
 *   g.busy(btn, false)  - restore the original content + enabled state
 *
 * `target` is a button/element or a selector. Turning busy on twice is a
 * no-op. The spinner REPLACES the button's icon (`<i>`/`<svg>` direct
 * child) and the label stays - never two icons; a button without an icon
 * shows the spinner alone. A form control (input/select) only goes
 * disabled + aria-busy. Turning busy off puts the ICON back in the
 * spinner's place (or the original content, for a button that had no
 * icon) - ONLY while the spinner is still there: content the action
 * rewrote meanwhile (an optimistic icon/label swap, a re-rendered button)
 * is kept as it is (BSP's report, 0.43.1: the release used to restore the
 * snapshot whole and undo the swap). g.action drives this for every
 * control that starts async work; former's submit pipeline rides it; free
 * for app code too.
 */
export function busy(target, on = true) {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	if (!el) return;
	if (on) {
		if (el._busy) return;
		el._busy = { disabled: el.disabled, icon: null, spinner: null, nodes: null };
		// Freeze the current width so the spinner does not reflow the layout.
		el.style.width = el.getBoundingClientRect().width + 'px';
		el.disabled = true;
		el.setAttribute('aria-busy', 'true');
		if (el.matches('input, select, textarea')) return;
		const spinner = document.createElement('span');
		spinner.className = 'spinner-border spinner-border-sm';
		spinner.setAttribute('aria-hidden', 'true');
		const icon = el.querySelector(':scope > i, :scope > svg');
		if (icon) {
			for (const c of icon.classList) if (/^m[se]-/.test(c)) spinner.classList.add(c);
			el._busy.icon = icon;
			icon.replaceWith(spinner);
		} else {
			el._busy.nodes = Array.from(el.childNodes);
			el.replaceChildren(spinner);
		}
		el._busy.spinner = spinner;
	} else {
		if (!el._busy) return;
		const { spinner, icon, nodes } = el._busy;
		if (spinner && spinner.parentNode === el) {
			// the spinner is still ours: the icon (or the whole original
			// content) goes back in its place; a rewritten button is left alone
			if (icon) spinner.replaceWith(icon);
			else el.replaceChildren(...nodes);
		}
		el.disabled = el._busy.disabled;
		el.style.width = '';
		el.removeAttribute('aria-busy');
		delete el._busy;
	}
}
